import { getTrail } from "@/lib/site-structure";
import { NAVIGATION_CONFIG, type MegaMenuConfig } from "@/lib/navigation/config";

/** A menu's own section: its hub (the bottom strip's first link) and the pages its columns list. */
function sectionPages(menu: MegaMenuConfig): string[] {
  return menu.sections.flatMap((section) => [
    ...(section.href ? [section.href] : []),
    ...(section.items ?? []).map((item) => item.href),
  ]);
}

function normalise(path: string): string {
  const bare = path.split(/[?#]/, 1)[0];
  return bare.length > 1 ? bare.replace(/\/$/, "") : bare;
}

/**
 * The header menu a page belongs to, for the "you are here" marker: a hub
 * marks its own menu, a page listed in a menu marks that menu (the first one,
 * for a page listed twice), any other page the nearest hub in its trail, and
 * failing that the nearest page in its trail that a menu lists (the Bundesland
 * pages sit under Ökopunkte, which heads a Maßnahmen column).
 */
export function getMenuIdForPath(path: string): string | undefined {
  const page = normalise(path);
  const hubOf = (p: string) =>
    NAVIGATION_CONFIG.find((menu) => menu.bottomStrip?.primary.href === p)?.id;

  const asHub = hubOf(page);
  if (asHub) return asHub;

  const listing = (p: string) =>
    NAVIGATION_CONFIG.find((menu) => sectionPages(menu).includes(p))?.id;

  const listedIn = listing(page);
  if (listedIn) return listedIn;

  const above = getTrail(page)
    .slice(0, -1)
    .reverse()
    .map((crumb) => crumb.path);
  return above.map(hubOf).find(Boolean) ?? above.map(listing).find(Boolean);
}
